import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../config/queryClient';
import { updateTaskStage } from 'services/case/caseServices';
import { updateTaskDetails } from 'services/CaseServices';

export const useMoveCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moveData) => {
      const { sourceCardId, sourceColumnId, targetColumnId } = moveData;

      // Skip API call if moving within the same column (just reordering)
      if (sourceColumnId === targetColumnId) {
        return { success: true, sameColumn: true };
      }

      // Different column - update task_stage via API
      const { stageUpdate } = require('services/TaskServices');
      return stageUpdate('task#' + sourceCardId, targetColumnId, false, true, true);
    },

    onMutate: async (moveData) => {
      const { sourceCardId, sourceColumnId, targetColumnId, targetCardId, edge, position, filters = {} } = moveData;

      await queryClient.cancelQueries([...queryKeys.tasks.column(sourceColumnId), filters]);
      if (sourceColumnId !== targetColumnId) {
        await queryClient.cancelQueries([...queryKeys.tasks.column(targetColumnId), filters]);
      }

      const previousSourceData = queryClient.getQueryData([...queryKeys.tasks.column(sourceColumnId), filters]);
      const previousTargetData =
        sourceColumnId === targetColumnId
          ? previousSourceData
          : queryClient.getQueryData([...queryKeys.tasks.column(targetColumnId), filters]);

      // Find and extract the moved card
      let movedCard = null;
      let cardWasFound = false;

      // Check if moving within the same column
      const isSameColumn = sourceColumnId === targetColumnId;

      if (isSameColumn) {
        // SAME COLUMN: Reorder without changing totalCount
        queryClient.setQueryData([...queryKeys.tasks.column(sourceColumnId), filters], (old) => {
          if (!old?.pages) return old;

          // Flatten all items to find source and target
          let allItems = [];
          old.pages.forEach((page) => {
            allItems = allItems.concat(page.items);
          });

          // Find source and target indices
          const sourceIndex = allItems.findIndex((item) => item.task_id === sourceCardId);
          const targetIndex = targetCardId ? allItems.findIndex((item) => item.task_id === targetCardId) : -1;

          if (sourceIndex === -1) {
            return old; // Card not found, no changes
          }

          // Remove from source position
          const [removed] = allItems.splice(sourceIndex, 1);

          // Calculate insert position based on edge
          let insertIndex = targetIndex;
          if (targetIndex !== -1) {
            if (edge === 'bottom') {
              insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
            } else {
              // edge === 'top'
              insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
            }
          } else if (position === 'end') {
            insertIndex = allItems.length;
          } else {
            insertIndex = 0;
          }

          // Insert at new position
          allItems.splice(insertIndex, 0, removed);

          // Reconstruct pages with reordered items (keep same page structure)
          let itemIndex = 0;
          const updatedPages = old.pages.map((page) => {
            const pageItemCount = page.items.length;
            const pageItems = allItems.slice(itemIndex, itemIndex + pageItemCount);
            itemIndex += pageItemCount;
            return { ...page, items: pageItems };
          });

          return { ...old, pages: updatedPages };
        });
      } else {
        // DIFFERENT COLUMN: Remove from source, add to target

        // UPDATE 1: Remove from source column and update totalCount
        queryClient.setQueryData([...queryKeys.tasks.column(sourceColumnId), filters], (old) => {
          if (!old?.pages) return old;

          // First pass: remove card and check if found
          const updatedPages = old.pages.map((page) => {
            const updatedItems = page.items.filter((task) => {
              if (task.task_id === sourceCardId) {
                movedCard = { ...task, task_stage: targetColumnId };
                cardWasFound = true;
                return false; // Remove from source
              }
              return true;
            });

            return { ...page, items: updatedItems };
          });

          // Second pass: update totalCount for all pages if card was found
          const finalPages = updatedPages.map((page) => ({
            ...page,
            totalCount: cardWasFound ? Math.max(0, (page.totalCount || 0) - 1) : page.totalCount,
          }));

          return { ...old, pages: finalPages };
        });

        // UPDATE 2: Add to target column and update totalCount
        if (movedCard) {
          queryClient.setQueryData([...queryKeys.tasks.column(targetColumnId), filters], (old) => {
            if (!old?.pages || !old.pages.length) return old;

            const updatedPages = [...old.pages];

            if (position === 'end') {
              // Add to last page
              const lastPageIndex = updatedPages.length - 1;
              updatedPages[lastPageIndex] = {
                ...updatedPages[lastPageIndex],
                items: [...updatedPages[lastPageIndex].items, movedCard],
              };
            } else {
              // Add to first page (most common case)
              updatedPages[0] = {
                ...updatedPages[0],
                items: [movedCard, ...updatedPages[0].items],
              };
            }

            // Update totalCount for all pages (increase by 1)
            const finalPages = updatedPages.map((page) => ({
              ...page,
              totalCount: (page.totalCount || 0) + 1,
            }));

            return { ...old, pages: finalPages };
          });
        }
      }

      return {
        previousSourceData,
        previousTargetData,
        sourceColumnId,
        targetColumnId,
        filters,
      };
    },

    onError: (err, moveData, context) => {
      console.error('Move card failed:', err);

      // Rollback source cache
      if (context?.previousSourceData) {
        queryClient.setQueryData([...queryKeys.tasks.column(context.sourceColumnId), context.filters], context.previousSourceData);
      }

      // Rollback target cache (if different)
      if (context?.sourceColumnId !== context?.targetColumnId && context?.previousTargetData) {
        queryClient.setQueryData([...queryKeys.tasks.column(context.targetColumnId), context.filters], context.previousTargetData);
      }
    },

    onSettled: (data, error, variables, context) => {
      if (context?.sourceColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.column(context.sourceColumnId), context.filters],
          refetchType: 'none', // Don't refetch immediately, just mark as stale
        });
      }
      if (context?.targetColumnId && context.targetColumnId !== context.sourceColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.column(context.targetColumnId), context.filters],
          refetchType: 'none', // Don't refetch immediately, just mark as stale
        });
      }
      if (variables.sourceCardId) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.all, 'detail', variables.sourceCardId],
          refetchType: 'none', // Don't refetch immediately
        });
      }
      // Mark initial-load query as stale (it caches first page of all columns)
      if (context?.filters) {
        queryClient.invalidateQueries({
          queryKey: ['tasks', 'initial-load', context.filters],
          refetchType: 'none', // Don't refetch immediately
        });
      }
    },
  });
};

export const useUpdateCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (moveData) => {
      const { cardId, data } = moveData;
      return updateTaskDetails(cardId, null, data, false, false, true, true);
    },

    onMutate: async (moveData) => {
      const { cardId, columnId, data, filters = {} } = moveData;

      // Cancel outgoing queries to prevent race conditions (include filters)
      await queryClient.cancelQueries([...queryKeys.tasks.column(columnId), filters]);

      // Store previous data for rollback (ONLY ONE CACHE NOW!)
      const previousData = queryClient.getQueryData([...queryKeys.tasks.column(columnId), filters]);

      // UPDATE: Infinite query cache (ALL pages)
      queryClient.setQueryData([...queryKeys.tasks.column(columnId), filters], (old) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((task) => (task.task_id === cardId ? { ...task, ...data } : task)),
          })),
        };
      });

      return { previousData, columnId, filters };
    },

    onError: (err, variables, context) => {
      console.error('Update card failed:', err);

      // Rollback single cache on error
      if (context?.previousData) {
        queryClient.setQueryData([...queryKeys.tasks.column(context.columnId), context.filters], context.previousData);
      }
    },

    onSettled: (data, error, variables, context) => {
      if (context?.columnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.column(context.columnId), context.filters],
          refetchType: 'none', // Don't refetch immediately, just mark as stale
        });
      }
      if (variables.cardId) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.tasks.all, 'detail', variables.cardId],
          refetchType: 'none', // Don't refetch immediately
        });
      }
      // Mark initial-load query as stale (it caches first page of all columns)
      if (context?.filters) {
        queryClient.invalidateQueries({
          queryKey: ['tasks', 'initial-load', context.filters],
          refetchType: 'none', // Don't refetch immediately
        });
      }
    },
  });
};

export const useReorderColumnsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reorderData, taskStages }) => {
      const { updateCaseStagePosition } = require('services/Settings');

      const { sourceIndex, targetIndex, edge } = reorderData;
      const updatedColumns = [...taskStages];
      const [movedColumn] = updatedColumns.splice(sourceIndex, 1);

      let insertIndex = targetIndex;
      if (edge === 'right') {
        insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
      } else if (edge === 'left') {
        insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      }

      updatedColumns.splice(insertIndex, 0, movedColumn);

      updateCaseStagePosition({ updated_positions: updatedColumns?.map((v, i) => ({ ...v, position: i })) }, true);
    },
    onMutate: async ({ reorderData }) => {
      const { sourceIndex, targetIndex, edge } = reorderData;

      await queryClient.cancelQueries(queryKeys.taskStages.list);

      const previousData = queryClient.getQueryData(queryKeys.taskStages.list());

      queryClient.setQueryData(queryKeys.taskStages.list(), (old) => {
        if (!old) return old;

        const updatedColumns = [...old];
        const [movedColumn] = updatedColumns.splice(sourceIndex, 1);

        let insertIndex = targetIndex;
        if (edge === 'right') {
          insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
        } else if (edge === 'left') {
          insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
        }

        updatedColumns.splice(insertIndex, 0, movedColumn);

        return updatedColumns?.map((v, i) => ({ ...v, position: i }));
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      console.error('Reorder columns failed:', err);
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.taskStages.list(), context.previousData);
      }
    },
  });
};

export const useRenameColumnMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sk, pk, newName, task_stage_id }) => {
      return updateTaskStage({
        pk: pk,
        sk: sk,
        data: { task_stage_name: newName, sk, pk, task_stage_id },
        excludeFromLoading: true,
      });
    },
    onMutate: async ({ sk, newName, task_stage_id }) => {
      await queryClient.cancelQueries(queryKeys.taskStages.list);

      const previousData = queryClient.getQueryData(queryKeys.taskStages.list());

      queryClient.setQueryData(queryKeys.taskStages.list(), (old) => {
        if (!old) return old;
        return old?.map((stage) => (stage.task_stage_id === task_stage_id ? { ...stage, task_stage_name: newName } : stage)) || [];
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      console.error('Rename column failed:', err);
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.taskStages.list(), context.previousData);
      }
    },
  });
};

export const useDeleteColumnMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ columnId, pk }) => {
      const { deleteTaskStage } = require('services/TaskServices');
      return deleteTaskStage(pk, 'task_stage#' + columnId, 'INACTIVE', true);
    },
    onMutate: async ({ columnId }) => {
      await queryClient.cancelQueries(queryKeys.taskStages.list);

      const previousData = queryClient.getQueryData(queryKeys.taskStages.list());

      queryClient.setQueryData(queryKeys.taskStages.list(), (old) => {
        if (!old) return old;
        return old?.filter((stage) => stage.task_stage_id !== columnId) || [];
      });

      return { previousData };
    },
    onSuccess: () => {
      // Don't invalidate - optimistic updates are enough
      // Both board and leadStages caches are already updated
    },
    onError: (err, variables, context) => {
      console.error('Archive column failed:', err);
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.taskStages.list(), context.previousData);
      }
    },
  });
};
